// Client Booking History PDF — premium dark A4 export (additive; does not replace Report PDF)
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  BRAND, rgb, fmtINR, fmtDate, computeDocHash, drawDiamond,
} from './shared';
import { formatHoursHm } from '../durationHours';
import { computeBookingTaxQuote } from '../gst/gstRules';

const COMPANY = {
  name: 'DEVELOPMENT EXPRESS',
  legal: 'Development Express Pvt Ltd',
  addr: 'Karad, Maharashtra',
  phone: '+91-8408000084',
  email: 'om.chavan2026@zohomail.in',
  web: 'developmentexpress.in',
};

const DARK = {
  BG: BRAND.COVER_BG,
  PANEL: '#16140F',
  ROW: '#1A1812',
  ROW_ALT: '#12100C',
  BORDER: '#3A3424',
  TEXT: '#F5EFD9',
  MUTED: '#A89870',
  GOLD: BRAND.COVER_GOLD,
  GOLD_LT: BRAND.COVER_GOLD_LT,
};

const statusColor = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('cancel')) return { bg: '#3A1A14', fg: '#E8A090' };
  if (s.includes('complete') || s.includes('done')) return { bg: '#14301F', fg: '#8FDBB0' };
  if (s.includes('active') || s.includes('pending')) return { bg: '#1A2A40', fg: '#9CB8E0' };
  return { bg: '#3A2E14', fg: '#E8C97A' };
};

function parseRowDate(row) {
  const raw = row?.sortDate || row?.raw?.start_date || row?.raw?.created_at || row?.date;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatPeriodDate(d) {
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Premium dark A4 booking history PDF for clients.
 * @param {Object} options
 * @param {Array} options.bookings - normalised rows: {id,date,machine,type,hours,baseAmt,gst,total,status}
 * @param {string} [options.clientName]
 * @param {string} [options.clientPhone]
 * @param {number} [options.cancelledCount] - cancelled count when bookings are pre-filtered
 * @param {boolean} [options.download]
 */
export async function generateClientBookingHistoryPDF({
  bookings = [],
  clientName = 'Client',
  clientPhone = '',
  cancelledCount = 0,
  download = true,
} = {}) {
  const rows = Array.isArray(bookings) ? bookings : [];
  const totalBase = rows.reduce((a, b) => a + Number(b.baseAmt || b.amount || 0), 0);
  const totalGST = rows.reduce((a, b) => a + Number(b.gst ?? computeBookingTaxQuote(b.baseAmt || 0).gst), 0);
  const grandTotal = rows.reduce((a, b) => a + Number(b.total || 0), 0);
  const completedCount = rows.filter((b) => {
    const s = String(b.status || '').toLowerCase();
    return s.includes('complete') || s.includes('done');
  }).length;
  const cancelledInRows = rows.filter((b) => String(b.status || '').toLowerCase().includes('cancel')).length;
  const cancelled = Number.isFinite(Number(cancelledCount))
    ? Math.max(0, Math.floor(Number(cancelledCount)))
    : cancelledInRows;

  const dated = rows.map(parseRowDate).filter(Boolean).sort((a, b) => a - b);
  const firstDate = dated[0] || null;
  const lastDate = dated[dated.length - 1] || null;
  const generatedOn = new Date().toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
  const fileDate = new Date().toISOString().slice(0, 10);
  const year = new Date().getFullYear();
  const reportId = `DE/HIST/CLI/${year}/${String(rows.length).padStart(4, '0')}`;
  const docHash = await computeDocHash(reportId, clientName, grandTotal);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  const paintPage = () => {
    doc.setFillColor(...rgb(DARK.BG));
    doc.rect(0, 0, w, h, 'F');
    doc.setFillColor(...rgb(DARK.GOLD));
    doc.rect(0, 0, w, 3.5, 'F');
    doc.setFillColor(...rgb(BRAND.GOLD_DEEP));
    doc.rect(0, 3.5, w, 0.4, 'F');
  };

  paintPage();
  let y = 16;

  // —— Header ——
  doc.setTextColor(...rgb(DARK.GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(COMPANY.name, 15, y);
  y += 7;
  doc.setTextColor(...rgb(DARK.TEXT));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Client Booking History Report', 15, y);
  y += 6;
  doc.setTextColor(...rgb(DARK.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`Generated: ${generatedOn}`, 15, y);
  y += 4.5;
  doc.text(
    `Period: ${formatPeriodDate(firstDate)} – ${formatPeriodDate(lastDate)}`,
    15,
    y,
  );
  doc.setTextColor(...rgb(DARK.GOLD_LT));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text(reportId, w - 15, 16, { align: 'right' });
  doc.setTextColor(...rgb(DARK.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text(docHash.substring(0, 12), w - 15, 20, { align: 'right' });

  y += 6;
  doc.setDrawColor(...rgb(DARK.GOLD));
  doc.setLineWidth(0.35);
  doc.line(15, y, w / 2 - 4, y);
  doc.line(w / 2 + 4, y, w - 15, y);
  drawDiamond(doc, w / 2, y, 1.3, DARK.GOLD);
  y += 8;

  // —— Client Summary ——
  doc.setFillColor(...rgb(DARK.PANEL));
  doc.setDrawColor(...rgb(DARK.BORDER));
  doc.setLineWidth(0.2);
  doc.roundedRect(15, y, w - 30, 34, 2, 2, 'FD');

  doc.setTextColor(...rgb(DARK.GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('CLIENT SUMMARY', 19, y + 6);

  doc.setTextColor(...rgb(DARK.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Client Name', 19, y + 13);
  doc.text('Phone', w / 2 + 2, y + 13);
  doc.setTextColor(...rgb(DARK.TEXT));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(String(clientName || 'Client').slice(0, 42), 19, y + 18);
  doc.text(String(clientPhone || '—').slice(0, 24), w / 2 + 2, y + 18);

  doc.setTextColor(...rgb(DARK.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.text('Total Bookings', 19, y + 25);
  doc.text('Completed', 55, y + 25);
  doc.text('Cancelled', 90, y + 25);
  doc.text('Spent (excl. GST)', 125, y + 25);
  doc.text('GST', 165, y + 25);

  doc.setTextColor(...rgb(DARK.GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(String(rows.length), 19, y + 30.5);
  doc.setTextColor(...rgb('#8FDBB0'));
  doc.text(String(completedCount), 55, y + 30.5);
  doc.setTextColor(...rgb('#E8A090'));
  doc.text(String(cancelled), 90, y + 30.5);
  doc.setTextColor(...rgb(DARK.TEXT));
  doc.text(fmtINR(totalBase), 125, y + 30.5);
  doc.text(fmtINR(totalGST), 165, y + 30.5);

  y += 40;
  doc.setTextColor(...rgb(DARK.MUTED));
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('Grand Total (incl. GST)', 15, y);
  doc.setTextColor(...rgb(DARK.GOLD));
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(fmtINR(grandTotal), w - 15, y, { align: 'right' });
  y += 6;

  // —— Booking Table ——
  autoTable(doc, {
    startY: y,
    head: [['#', 'Booking Ref', 'Machine', 'Type', 'Date', 'Hours', 'Total', 'Status']],
    body: rows.map((b, i) => [
      String(i + 1),
      String(b.id || '—'),
      String(b.machine || b.machineName || '—'),
      String(b.type || '—'),
      String(b.date || (parseRowDate(b) ? fmtDate(parseRowDate(b).toISOString()) : '—')),
      formatHoursHm(b.hours || 0),
      fmtINR(Number(b.total || 0)),
      String(b.status || '—'),
    ]),
    styles: {
      font: 'helvetica',
      fontSize: 7,
      cellPadding: 2.4,
      valign: 'middle',
      textColor: rgb(DARK.TEXT),
      fillColor: rgb(DARK.ROW),
      lineColor: rgb(DARK.BORDER),
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: rgb('#2A2418'),
      textColor: rgb(DARK.GOLD),
      fontStyle: 'bold',
      fontSize: 6.5,
    },
    alternateRowStyles: { fillColor: rgb(DARK.ROW_ALT) },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 28, fontStyle: 'bold', textColor: rgb(DARK.GOLD_LT) },
      2: { cellWidth: 32 },
      3: { cellWidth: 20 },
      4: { cellWidth: 24 },
      5: { cellWidth: 16, halign: 'center' },
      6: { cellWidth: 24, halign: 'right', fontStyle: 'bold', textColor: rgb(DARK.GOLD) },
      7: { cellWidth: 22, halign: 'center' },
    },
    margin: { left: 15, right: 15, top: 12, bottom: 28 },
    theme: 'plain',
    didParseCell: (hook) => {
      if (hook.section === 'body' && hook.column.index === 7) {
        const { bg, fg } = statusColor(hook.cell.raw);
        hook.cell.styles.fillColor = rgb(bg);
        hook.cell.styles.textColor = rgb(fg);
        hook.cell.styles.fontStyle = 'bold';
      }
    },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) paintPage();
    },
  });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    // Footer
    doc.setDrawColor(...rgb(DARK.GOLD));
    doc.setLineWidth(0.3);
    doc.line(15, h - 18, w / 2 - 4, h - 18);
    doc.line(w / 2 + 4, h - 18, w - 15, h - 18);
    drawDiamond(doc, w / 2, h - 18, 1.2, DARK.GOLD);

    doc.setTextColor(...rgb(DARK.MUTED));
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6.5);
    doc.text(
      'This is a system-generated report. Not valid as GST invoice.',
      w / 2,
      h - 12,
      { align: 'center' },
    );
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.text(
      `${COMPANY.legal} | ${COMPANY.addr}`,
      w / 2,
      h - 7.5,
      { align: 'center' },
    );
    doc.setFontSize(6);
    doc.text(`Page ${i} of ${totalPages}`, w - 15, h - 7.5, { align: 'right' });
  }

  const filename = `DE_Booking_History_${fileDate}.pdf`;
  if (download) doc.save(filename);
  doc.__deFilename = filename;
  return doc;
}
